import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './confirmModal.scss';
import adjustLUIStyle from '../../core/utils/adjustLUIStyle';

const ConfirmModal = ({
    isOpen,
    message,
    title = 'Confirm Action',
    onOk,
    onCancel,
    onClose,
    okText = 'OK',
    cancelText = 'Cancel',
    showCancel = true
}) => {
    const modalRef = useRef(null);

    useEffect(() => {
        adjustLUIStyle(document.querySelectorAll('.confirm-modal__button'), 'lui-button');
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            // Prevent background scrolling when modal is open
            document.body.style.overflow = 'hidden';
            // Focus the modal container for keyboard events
            if (modalRef.current) {
                modalRef.current.focus();
            }
        } else {
            document.body.style.overflow = 'unset';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleOk = () => {
        if (onOk) onOk();
        if (onClose) onClose();
    };

    const handleCancel = () => {
        if (onCancel) onCancel();
        if (onClose) onClose();
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleCancel();
        } else if (e.key === 'Enter') {
            handleOk();
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="confirm-modal__overlay" 
            onClick={handleOverlayClick}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            aria-describedby="confirm-modal-message"
            ref={modalRef}
        >
            <div className="confirm-modal__container">
                <div className="confirm-modal__header">
                    <h3 id="confirm-modal-title" className="confirm-modal__title">
                        {title}
                    </h3>
                </div>
                
                <div className="confirm-modal__body">
                    <p id="confirm-modal-message" className="confirm-modal__message">
                        {message}
                    </p>
                </div>
                
                <div className="confirm-modal__footer">
                    {showCancel && (
                        <lui-button 
                            class="confirm-modal__button confirm-modal__button--cancel"
                            onClick={handleCancel}
                            secondary
                        >
                            {cancelText}
                        </lui-button>
                    )}
                    <lui-button 
                        class="confirm-modal__button confirm-modal__button--ok"
                        onClick={handleOk}
                    >
                        {okText}
                    </lui-button>
                </div>
            </div>
        </div>
    );
};

ConfirmModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    message: PropTypes.string.isRequired,
    title: PropTypes.string,
    onOk: PropTypes.func,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    okText: PropTypes.string,
    cancelText: PropTypes.string,
    showCancel: PropTypes.bool
};

export default ConfirmModal;